import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Users, ShieldCheck, Search } from "lucide-react";

export default function PlatformUsers() {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");

  const load = (query = "") => api.get("/platform/users", { params: query ? { q: query } : {} }).then((r) => setUsers(r.data.users)).catch(() => {});
  useEffect(() => { load(); }, []);

  const toggleAdmin = async (u) => {
    const next = u.platform_role === "operix_admin" ? null : "operix_admin";
    if (!window.confirm(next ? `Grant Platform Admin to ${u.email}?` : `Revoke Platform Admin from ${u.email}?`)) return;
    try {
      await api.post(`/platform/users/${u.id}/platform-role`, { platform_role: next });
      toast.success("Platform role updated");
      load(q);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="p-6 lg:p-10 max-w-5xl" data-testid="platform-users-page">
      <div className="flex items-center gap-2 mb-1">
        <Users className="w-5 h-5 text-emerald-400" />
        <h1 className="text-2xl font-bold tracking-tight">Platform Users</h1>
      </div>
      <p className="text-slate-400 text-sm mb-6">Global identities across all companies. Platform Admin authority is separate from company Super Admin.</p>

      <div className="relative max-w-sm mb-5">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
        <input
          value={q}
          onChange={(e) => { setQ(e.target.value); load(e.target.value); }}
          placeholder="Search email or name…"
          data-testid="user-search"
          className="w-full bg-slate-900 border border-white/10 rounded-lg pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
        />
      </div>

      <div className="overflow-x-auto rounded-xl ring-1 ring-white/5">
        <table className="w-full text-sm" data-testid="platform-users-table">
          <thead className="bg-slate-900/80 text-slate-400 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-3">User</th>
              <th className="text-left px-4 py-3">Companies</th>
              <th className="text-left px-4 py-3">Platform role</th>
              <th className="text-right px-4 py-3">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {users.map((u) => (
              <tr key={u.id} className="hover:bg-white/5" data-testid={`user-row-${u.id}`}>
                <td className="px-4 py-3">
                  <div className="font-medium">{u.name || u.email}</div>
                  <div className="text-xs text-slate-500">{u.email}</div>
                </td>
                <td className="px-4 py-3 tabular-nums text-slate-300">{u.company_count}</td>
                <td className="px-4 py-3">
                  {u.platform_role === "operix_admin" ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-500/15 text-emerald-300">
                      <ShieldCheck className="w-3 h-3" /> Platform Admin
                    </span>
                  ) : (
                    <span className="text-slate-500 text-xs">—</span>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  <button
                    onClick={() => toggleAdmin(u)}
                    data-testid={`toggle-admin-${u.id}`}
                    className={`text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors ${u.platform_role === "operix_admin" ? "bg-rose-500/10 text-rose-300 hover:bg-rose-500/20" : "bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20"}`}
                  >
                    {u.platform_role === "operix_admin" ? "Revoke admin" : "Make admin"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
